import isMobile from 'ismobilejs'
import debounce from 'lodash/debounce'

export const useUserList = defineStore('userList', () => {
  const { $axios } = useNuxtApp()

  class UserListInnerProduct {
    constructor(data) {
      for (const [field, value] of Object.entries(data)) {
        this[field] = value
      }
    }

    get product_id() {
      return this.id
    }
  }

  class UserListProduct {
    constructor(userList, data) {
      this.id = data.id
      this.note = data.note
      this.rotate_for_insert = data.rotate_for_insert
      this.userListId = userList.id
      this.product = new UserListInnerProduct(data.product)
    }

    get userList() {
      return lookupList(this.userListId)
    }

    async updateNote(note) {
      await $axios.put(`/api/customer/list-products/${this.id}/`, {
        note: note,
      })
      this.note = note
    }

    async delete() {
      await $axios.delete(`/api/customer/list-products/${this.id}/`)
      this.userList.products = this.userList.products.filter(
        (i) => i.id !== this.id,
      )
      if (productStatuses.value[this.product.id]) {
        productStatuses.value[this.product.id].lists = productStatuses.value[
          this.product.id
        ].lists.filter((i) => i.id != this.userListId)
      }
    }
  }

  class UserList {
    constructor(data) {
      this.id = data.id
      this.products = null
      this.updateFields(data)
    }

    updateFields(data) {
      this.name = data.name
      this.creator = data.creator
      this.description = data.description
      this.shareLink = data.share_link
    }

    async getProducts() {
      if (this.products === null) {
        let next = `/api/customer/list-products/${this.id}/products/`
        const products = []
        while (next) {
          const response = await $axios.get(next)
          for (const product of response.data.results) {
            products.push(new UserListProduct(this, product))
          }
          next = response.data.next
        }
        this.products = products
      }
      return this.products
    }

    async findProduct(product) {
      await this.getProducts()
      return this.products.find((p) => p.product.id === product.product_id)
    }

    async addProduct(product) {
      const response = await $axios.post('/api/customer/list-products/add/', {
        user_list: this.id,
        product: product.product_id,
      })
      if (this.products) {
        this.products.push(new UserListProduct(this, response.data))
      }
      if (productStatuses.value[product.product_id]) {
        productStatuses.value[product.product_id].lists.push(this)
      } else {
        queueProductStatus(product.product_id)
      }
    }

    async modify(name, creator, description = '') {
      const response = await $axios.put(`/api/customer/lists/${this.id}/`, {
        name,
        creator,
        description,
      })
      this.name = response.data.name
      this.creator = response.data.creator
      this.description = response.data.description
    }

    async delete() {
      await $axios.delete(`/api/customer/lists/${this.id}/`)
      myLists.value = myLists.value.filter((i) => i.id !== this.id)
      for (const status of Object.values(productStatuses.value)) {
        status.lists = status.lists.filter((i) => i.id != this.id)
      }
    }

    async getShareLink() {
      if (!this.shareLink) {
        const link = await $axios.get(
          `/api/customer/lists/${this.id}/get_share_link/`,
        )
        this.shareLink = link.data.share_url
      }
    }

    serializeForPLP() {
      return {
        id: this.id,
        name: this.name,
        creator: this.creator,
        description: this.description,
      }
    }
  }

  // --- User Lists ---
  const listCache = ref({})
  const listByHash = ref({})
  const myLists = ref([])
  let myListsLoaded = false

  // --- Heart Icon ---
  const productStatuses = ref({})
  const productsToFetch = new Set()
  const activeHeartProduct = ref(null)

  // --- Share Modal ---
  const showShareModal = ref(false)
  const sharingList = ref({})

  // --- Toast State ---
  const isToastVisible = ref(false)
  const toastData = reactive({
    notificationStatus: null, // 'created' or 'canceled'
    listsAdded: 0,
    listsRemoved: 0,
  })
  let toastTimeoutId = null

  const storeList = (data) => {
    if (listCache.value[data.id]) {
      listCache.value[data.id].updateFields(data)
    } else {
      const userList = new UserList(data)
      listCache.value[data.id] = userList
    }
    return listCache.value[data.id]
  }

  const fetchMyLists = async (force = false) => {
    if (myListsLoaded && !force) return
    try {
      const response = await $axios.get('/api/customer/lists/')

      // Avoid race conditions
      if (myListsLoaded && !force) return

      // Clear any previously loaded lists
      myLists.value.length = 0

      for (const data of response.data) {
        const userList = storeList(data)
        myLists.value.push(userList)
      }
      myListsLoaded = true
    } catch (error) {
      console.error('Failed to fetch user lists:', error)
    }
  }

  const lookupList = (listId) => {
    return listCache.value[listId] || listCache.value[listByHash.value[listId]]
  }

  const getList = async (listId) => {
    if (listCache.value[listId] === undefined) {
      const response = await $axios.get(`/api/customer/lists/${listId}/`)
      storeList(response.data)
    }
    return lookupList(listId)
  }

  const getListByHash = async (hash) => {
    if (listByHash.value[hash]) {
      return listCache.value[listByHash.value[hash]]
    }
    const response = await $axios.get(`/api/customer/list/${hash}/`)
    const existingList = lookupList(response.data.id)
    if (existingList) {
      listByHash.value[hash] = existingList.id
      return existingList
    }
    const userList = storeList(response.data)
    listByHash.value[hash] = userList.id
    return userList
  }

  const createList = async (name, creator, description) => {
    const response = await $axios.post('/api/customer/lists/', {
      name,
      creator,
      description,
    })
    const userList = storeList(response.data)
    myLists.value.push(userList)

    return userList
  }

  const fetchProductStatuses = debounce(async () => {
    if (productsToFetch.size === 0) return

    const ids = Array.from(productsToFetch)
    productsToFetch.clear()

    await fetchMyLists()

    try {
      const response = await $axios.get('/api/customer/lists/product_lists/', {
        params: { products: ids.join(',') },
      })
      for (const [productId, value] of Object.entries(response.data)) {
        const refList = []
        for (const listId of value.lists) {
          refList.push(listCache.value[listId])
        }
        value.lists = refList
        productStatuses.value[productId] = value
      }
    } catch (error) {
      console.error('Failed to batch-fetch product statuses:', error)
    }
  }, 100)

  const queueProductStatus = (productId, force = false) => {
    if (!force && productStatuses.value[productId] !== undefined) {
      return
    }

    productsToFetch.add(productId)
    fetchProductStatuses()
  }

  const getProductStatus = (productId) => {
    return productStatuses.value[productId]
  }

  const updateSubscription = (user, subscription) => {
    const productId = subscription.product
    user.notifyme_products[productId] = subscription
    if (productStatuses.value[productId]) {
      productStatuses.value[productId].notify_me = subscription.is_active
    }
  }

  // --- Toast Actions ---
  const showToast = (details) => {
    if (toastTimeoutId) {
      clearTimeout(toastTimeoutId)
    }
    toastData.notificationStatus = details.notificationStatus
    toastData.listsAdded = details.listsAdded
    toastData.listsRemoved = details.listsRemoved
    isToastVisible.value = true

    toastTimeoutId = setTimeout(() => {
      hideToast()
    }, 5000)
  }

  const hideToast = () => {
    isToastVisible.value = false
    toastData.notificationStatus = null
    toastData.listsAdded = 0
    toastData.listsRemoved = 0
    toastTimeoutId = null
  }

  const shareList = async (userList) => {
    await userList.getShareLink()

    if (isMobile().any) {
      window.navigator.share({
        url: userList.shareLink,
        title: `${userList.creator} shared a wine list with you - plus $50 off at Wine Access`,
        text: `I've put together a list of wines I think you'll love: ${userList.name}. When you order from Wine Access, you'll get $50 off your first purchase of $150 or more - and trust me, they're by far the best way to discover and enjoy the world's best wines.`,
      })
    } else {
      sharingList.value = userList
      showShareModal.value = true
    }
  }

  return {
    myLists,
    fetchMyLists,
    lookupList,
    getList,
    getListByHash,
    createList,
    queueProductStatus,
    getProductStatus,
    activeHeartProduct,
    updateSubscription,
    showShareModal,
    sharingList,
    shareList,
    isToastVisible,
    toastData,
    showToast,
    hideToast,
  }
})
